import { Outlet } from "react-router-dom";
import { lazy, Suspense, Component, ReactNode } from "react";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { AdminTopNav } from "@/components/admin/AdminTopNav";
import { AdminMobileHeader } from "@/components/admin/AdminMobileHeader";
import { MobileBottomNav } from "@/components/navigation/MobileBottomNav";
import { WindowManagerProvider } from "@/contexts/WindowManagerContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { SidebarProvider, useSidebar } from "@/contexts/SidebarContext";
import { LayoutProvider } from "@/contexts/LayoutContext";
import { AdminViewBanner } from "@/components/admin/AdminViewBanner";
import { cn } from "@/lib/utils";
import { SkipLink } from "@/components/accessibility/SkipLink";
import { logger } from "@/lib/logger";
import { PageTransition } from "@/components/layout/PageTransition";

// Simple error boundary for lazy-loaded components
interface LazyErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface LazyErrorBoundaryState {
  hasError: boolean;
}

class LazyErrorBoundary extends Component<LazyErrorBoundaryProps, LazyErrorBoundaryState> {
  constructor(props: LazyErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): LazyErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Lazy component failed to load', { error: error.message, componentStack: errorInfo.componentStack });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

// PERFORMANCE: Lazy load non-critical components
const AIAssistant = lazy(() => import('@/components/ai/AIAssistant').then(m => ({ default: m.AIAssistant })));
const WindowManager = lazy(() => import('@/components/admin/WindowManager').then(m => ({ default: m.WindowManager })));

function AdminLayoutContent() {
  const isMobile = useIsMobile();
  const { isCollapsed } = useSidebar();

  // Debug logging (development only)
  if (import.meta.env.DEV) {
    logger.debug('AdminLayoutContent: Rendering', {
      isMobile,
      isCollapsed,
      pathname: window.location.pathname
    });
  }

  return (
    <>
      {isMobile ? (
        <div className="min-h-dvh bg-background pb-16">
          <SkipLink />
          <AdminViewBanner />
          <header>
            <AdminMobileHeader />
          </header>
          <main
            id="main-content"
            data-scroll-container
            className="ios-smooth-scroll bg-background px-4 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] pt-4 sm:px-6"
          >
            <PageTransition variant="fade">
              <Outlet />
            </PageTransition>
          </main>
          <nav aria-label="Mobile navigation">
            <MobileBottomNav />
          </nav>
        </div>
      ) : (
        <div className="flex h-screen w-full overflow-hidden bg-background">
          <SkipLink />
          <aside 
            className={cn(
              "hidden md:flex bg-white transition-all duration-250 ease-in-out border-r border-border h-screen",
              isCollapsed ? "w-[72px]" : "w-[280px]"
            )} 
            aria-label="Main navigation"
          >
            <div className="sticky top-0 h-screen w-full">
              <AdminSidebar />
            </div>
          </aside>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <AdminViewBanner />
            <header>
              <AdminTopNav />
            </header>
            <main
              id="main-content"
              data-scroll-container
              className="ios-smooth-scroll min-h-0 flex-1 overflow-y-auto bg-background px-4 pb-8 pt-4 sm:px-6 md:pt-6 lg:px-8"
            >
              <PageTransition variant="fade">
                <Outlet />
              </PageTransition>
            </main>
          </div>
        </div>
      )}
    </>
  );
}

export function AdminLayout() {
  if (import.meta.env.DEV) {
    logger.debug('AdminLayout: Component rendering');
  }

  return (
    <SidebarProvider>
      <LayoutProvider>
        <WindowManagerProvider>
          <AdminLayoutContent />
          {/* PERFORMANCE: Lazy load WindowManager and AIAssistant with error boundaries */}
          <LazyErrorBoundary>
            <Suspense fallback={null}>
              <WindowManager />
            </Suspense>
          </LazyErrorBoundary>
          <LazyErrorBoundary>
            <Suspense fallback={null}>
              <AIAssistant />
            </Suspense>
          </LazyErrorBoundary>
        </WindowManagerProvider>
      </LayoutProvider>
    </SidebarProvider>
  );
}
